import { Copy, ThumbsDown, ThumbsUp } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { WidgetRenderer } from "@/components/widgets/WidgetRenderer";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useChatStore } from "@/stores/chat";
import type { Message, Uuid } from "@/types";

interface MessageBubbleProps {
  message: Message;
  showActions?: boolean;
  onWidgetSubmit?: (widget_id: Uuid, data: Record<string, unknown>) => void;
  onWidgetApproval?: (
    widget_id: Uuid,
    action: "save" | "cancel" | "approve" | "reject" | "request_change",
    reason?: string,
  ) => void;
}

const formatTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" });

export function MessageBubble({
  message,
  showActions = true,
  onWidgetSubmit,
  onWidgetApproval,
}: MessageBubbleProps): JSX.Element {
  const isUser = message.role === "user";

  if (isUser) {
    if (message.content.type !== "text") return <></>;
    return (
      <div className="flex justify-end animate-fade-in">
        <div className="flex flex-col items-end gap-1 max-w-[70%]">
          <div className="chat-bubble-user whitespace-pre-wrap">
            {message.content.text}
          </div>
          <span className="text-[10px] text-muted-foreground px-1">
            {formatTime(message.created_at)} · Anh
          </span>
        </div>
      </div>
    );
  }

  return (
    <article className="group animate-fade-in">
      <header className="flex items-center gap-2 mb-1.5 text-xs text-muted-foreground">
        <Avatar size="sm" alt="AI" fallback="◐" className="bg-primary text-primary-foreground" />
        <span className="font-medium text-foreground">Trợ lý AI</span>
        <span>·</span>
        <span>{formatTime(message.created_at)}</span>
      </header>
      <div className="chat-bubble-assistant pl-8 pr-2 prose prose-sm max-w-none dark:prose-invert">
        <MessageContent
          message={message}
          onWidgetSubmit={onWidgetSubmit}
          onWidgetApproval={onWidgetApproval}
        />
      </div>
      {showActions && message.content.type === "text" && message.content.text ? (
        <div className="pl-8 mt-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
          <Button variant="ghost" size="icon" className="size-7" aria-label="Hữu ích">
            <ThumbsUp className="size-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="size-7" aria-label="Không hữu ích">
            <ThumbsDown className="size-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label="Copy"
            onClick={() => {
              if (message.content.type === "text") {
                void navigator.clipboard.writeText(message.content.text);
              }
            }}
          >
            <Copy className="size-3.5" />
          </Button>
        </div>
      ) : null}
    </article>
  );
}

function MessageContent({
  message,
  onWidgetSubmit,
  onWidgetApproval,
}: {
  message: Message;
  onWidgetSubmit?: (widget_id: Uuid, data: Record<string, unknown>) => void;
  onWidgetApproval?: (
    widget_id: Uuid,
    action: "save" | "cancel" | "approve" | "reject" | "request_change",
    reason?: string,
  ) => void;
}): JSX.Element {
  const widgets = useChatStore((s) => s.widgets);

  if (message.content.type === "text") {
    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]}>
        {message.content.text || "_(đang nghĩ...)_"}
      </ReactMarkdown>
    );
  }
  if (message.content.type === "widget") {
    const widgetId = message.metadata?.widget_id as Uuid | undefined;
    const status = widgetId ? widgets.get(widgetId)?.status ?? "pending" : "pending";
    return (
      <div className="not-prose -ml-8">
        <WidgetRenderer
          widgetId={widgetId ?? "unknown"}
          widgetType={message.content.widget_type}
          payload={message.content.payload}
          status={status}
          onSubmit={(data) => widgetId && onWidgetSubmit?.(widgetId, data)}
          onApproval={(action, reason) =>
            widgetId && onWidgetApproval?.(widgetId, action, reason)
          }
        />
      </div>
    );
  }
  return <></>;
}
